// GET /natal/chart · POST /natal/chart · GET /natal/houses · POST /natal/preview
import { z } from 'zod'
import { chartPointSchema, planetSchema, zodiacSignSchema } from '../enums.js'
import { aspectKindSchema } from '../domain/compatAspects.js'
import { idSchema, isoDateSchema, isoDateTimeSchema, timeOfDaySchema } from './common.js'

export const natalPlanetPositionSchema = z.object({
  planet: planetSchema,
  sign: zodiacSignSchema,
  degree: z.number().min(0).max(30), // градус внутри знака
  house: z.number().int().min(1).max(12).nullable(), // null без birth_time
})
export type NatalPlanetPosition = z.infer<typeof natalPlanetPositionSchema>

/**
 * Геометрия дома: номер, знак на куспиде, градус. Ровно то, что считают эфемериды и что
 * хранится в БД. Разбор (тексты) сюда НЕ входит: он собирается на чтении из справочника,
 * чтобы правка формулировки применялась ко всем картам сразу, без пересчёта и миграций.
 */
export const natalHouseSchema = z.object({
  number: z.number().int().min(1).max(12),
  sign: zodiacSignSchema,
  degree: z.number().min(0).max(30),
})
export type NatalHouse = z.infer<typeof natalHouseSchema>

/**
 * Дом с готовым разбором — то, что уходит на фронт. Фронт ничего не собирает сам:
 * `title`, `sphere` и `paragraphs` рендерятся как есть (правило «мапим, а не сочиняем»).
 * `paragraphs` — уже разбитый на абзацы текст: сфера дома → знак на куспиде → планеты
 * (или управитель, если дом пустой).
 */
export const natalHouseReadingSchema = natalHouseSchema.extend({
  /** Планеты, попавшие в этот дом. Порядок — как в `NatalChartDto.planets`. */
  planets: z.array(planetSchema),
  /** Название дома: «Дом денег». */
  title: z.string().min(1),
  /** Сфера жизни одной строкой — подзаголовок карточки. */
  sphere: z.string().min(1),
  paragraphs: z.array(z.string().min(1)).min(1),
  /** Знак на куспиде — отдельный таб «Дома в знаках». */
  sign_text: z.string().min(1).nullable(),
})
export type NatalHouseReading = z.infer<typeof natalHouseReadingSchema>

/** Строка-расшифровка карточки «Что я тут вижу»: `[Планета · Знак] [что это значит]`. */
export const natalHighlightSchema = z.object({
  label: z.string().min(1),
  text: z.string().min(1),
})
export type NatalHighlight = z.infer<typeof natalHighlightSchema>

/**
 * Карточка одной точки карты — слой «каждая точка отдельно».
 *
 * Точек больше, чем тел в `planets`: сюда попадают Уран, Нептун, Плутон, Северный узел и MC,
 * которых нет ни в `planetsJson`, ни в синастрии. Разбор собирается на чтении, поэтому
 * расширение состава ничего не ломает в уже посчитанных картах.
 */
export const natalPointReadingSchema = z.object({
  point: chartPointSchema,
  sign: zodiacSignSchema,
  /** Градус внутри знака, 0..30. Нужен и колесу, и табу «Информация». */
  degree: z.number().min(0).max(30),
  house: z.number().int().min(1).max(12).nullable(),
  is_retrograde: z.boolean(),
  /** «Солнце · Дева · 8 дом». */
  title: z.string().min(1),
  /**
   * Куски разбора раздельно — их разбирают разные табы («Планеты в знаках», «Планеты в домах»).
   * Склеенный массив абзацев пришлось бы резать на клиенте, домысливая, где кончается одно.
   */
  core: z.string().min(1),
  sign_text: z.string().min(1),
  house_text: z.string().min(1).nullable(),
  retrograde_text: z.string().min(1).nullable(),
})
export type NatalPointReading = z.infer<typeof natalPointReadingSchema>

/** Угол между двумя точками карты, уже с готовым текстом и орбом в виде «2°33′». */
export const natalAspectReadingSchema = z.object({
  a: chartPointSchema,
  b: chartPointSchema,
  kind: aspectKindSchema,
  title: z.string().min(1),
  orb: z.string().min(1),
  is_applying: z.boolean(),
  text: z.string().min(1),
})
export type NatalAspectReading = z.infer<typeof natalAspectReadingSchema>

/**
 * Таб «Информация» — сырьё, из которого сделаны выводы в остальных табах.
 *
 * Показываем фактами, а не баллами. Популярные сервисы складывают достоинства, дома и углы
 * в один «афетический» балл и печатают «Луна (−5)»; балл не следует из расчёта, это чья-то
 * шкала, а читается как приговор.
 */
export const natalDignitySchema = z.object({
  body: chartPointSchema,
  sign: zodiacSignSchema,
  /** Достоинства по традиционным управителям — тем же, на которых стоит вся карта. */
  traditional: z.array(z.string()),
  /** Что добавляет современная схема (Уран — Водолей, Плутон — Скорпион, Нептун — Рыбы). */
  modern: z.array(z.string()),
  house: z.number().int().min(1).max(12).nullable(),
  house_strength: z.string().nullable(),
  is_retrograde: z.boolean(),
})
export type NatalDignity = z.infer<typeof natalDignitySchema>

export const natalRulershipSchema = z.object({
  house: z.number().int().min(1).max(12),
  sign: zodiacSignSchema,
  ruler: planetSchema,
  ruler_house: z.number().int().min(1).max(12),
})
export type NatalRulership = z.infer<typeof natalRulershipSchema>

export const natalInfoSchema = z.object({
  elements: z.object({
    fire: z.number().int(),
    earth: z.number().int(),
    air: z.number().int(),
    water: z.number().int(),
  }),
  qualities: z.object({
    cardinal: z.number().int(),
    fixed: z.number().int(),
    mutable: z.number().int(),
  }),
  /** По скольким точкам считали стихии — набор у сервисов разный, и это надо называть. */
  counted: z.number().int(),
  dignities: z.array(natalDignitySchema),
  rulerships: z.array(natalRulershipSchema),
  /** Расшифровки терминов: обитель, экзальтация, угловой дом, попятность. */
  notes: z.record(z.string(), z.string()),
})
export type NatalInfo = z.infer<typeof natalInfoSchema>

// Без birth_time asc и houses = null — три кита показывают заглушку (см. 09_tech.md §3).
export const natalChartDtoSchema = z.object({
  computed_at: isoDateTimeSchema,
  planets: z.array(natalPlanetPositionSchema),
  houses: z.array(natalHouseReadingSchema).nullable(),
  asc: zodiacSignSchema.nullable(),
  /** Абзацы карточки «ЧТО Я ТУТ ВИЖУ» — Солнце, Луна, асцендент, стихии, главный дом. */
  summary: z.array(z.string().min(1)).min(1),
  highlights: z.array(natalHighlightSchema),
  /**
   * Глубокий разбор: опора карты, карточка на каждую точку, углы от точного к широкому.
   * Пустые массивы означают, что глубокий слой не собирался, — например у превью чужой
   * карты, где нет ни времени, ни города.
   */
  points: z.array(natalPointReadingSchema),
  aspects: z.array(natalAspectReadingSchema),
  /** Абзацы раздела «на чём карта стоит» — асцендент, его управитель, светила, точный угол. */
  foundation: z.array(z.string().min(1)),
  /** Таб «Информация». `null` — глубокий слой не собирался. */
  info: natalInfoSchema.nullable(),
})
export type NatalChartDto = z.infer<typeof natalChartDtoSchema>

// GET /natal/chart — из кэша.
export const getNatalChartResponseSchema = natalChartDtoSchema
export type GetNatalChartResponse = z.infer<typeof getNatalChartResponseSchema>

// POST /natal/chart — пересчёт (инвалидация при изменении данных рождения).
export const recalcNatalChartResponseSchema = natalChartDtoSchema
export type RecalcNatalChartResponse = z.infer<typeof recalcNatalChartResponseSchema>

// GET /natal/houses
export const getNatalHousesResponseSchema = z.object({
  houses: z.array(natalHouseReadingSchema).nullable(),
  asc: zodiacSignSchema.nullable(),
})
export type GetNatalHousesResponse = z.infer<typeof getNatalHousesResponseSchema>

/**
 * POST /natal/preview — карта на произвольные данные («построить другому человеку»).
 *
 * Считается на лету и **ничего не сохраняет**: расчёт — чистая функция, а чужие даты рождения
 * в продукте не хранятся вовсе (спека 05: «Данные чужих людей я никуда не отдаю — считаю
 * и забываю»). Поэтому у ручки нет ни `id`, ни отдельной таблицы, ни GET-двойника.
 *
 * Своя ручка, а не параметры к `GET /natal/chart`: тот отдаёт ровно одну карту — мою,
 * и кэшируется и на бэке (`NatalChart` уникален по `userId`), и на фронте ключом без входа.
 */
export const natalPreviewRequestSchema = z.object({
  birth_date: isoDateSchema,
  birth_time: timeOfDaySchema.optional(),
  birth_city_id: idSchema.optional(),
})
export type NatalPreviewRequest = z.infer<typeof natalPreviewRequestSchema>

export const natalPreviewResponseSchema = natalChartDtoSchema
export type NatalPreviewResponse = z.infer<typeof natalPreviewResponseSchema>
